import { AuthProviderProps } from 'react-oidc-context';
import { WebStorageStateStore } from 'oidc-client-ts';
import { resolveOAuthReturnPathFromUrl } from '../../app/constants/authRoutes';
import { usesOwnOrigin } from '../../config/ownOriginHosts';

function getWebBaseUrl(): string {
  // A brand site returns from sign-in and sign-out on its own domain.
  if (usesOwnOrigin()) return window.location.origin;
  const apiEnv = (process.env.REACT_APP_API_ENV || 'development').toLowerCase();
  if (apiEnv === 'staging') {
    return process.env.REACT_APP_WEB_URL_STAGING || 'https://staging.autopilotee.com';
  }
  if (apiEnv === 'production') {
    return process.env.REACT_APP_WEB_URL_PROD || 'https://autopilotee.com';
  }
  return process.env.REACT_APP_WEB_URL_DEV || 'http://localhost:3000';
}

function normalizeCognitoDomain(domain?: string): string | undefined {
  if (!domain) return undefined;
  const trimmed = domain.trim().replace(/\/$/, '');
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

const authorityEnv = process.env.REACT_APP_COGNITO_AUTHORITY as string | undefined;
const region = process.env.REACT_APP_COGNITO_REGION as string | undefined;
const userPoolId = process.env.REACT_APP_COGNITO_USER_POOL_ID as string | undefined;
const computedAuthority =
  authorityEnv ||
  (region && userPoolId ? `https://cognito-idp.${region}.amazonaws.com/${userPoolId}` : undefined);

const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID as string | undefined;
const clientSecret = process.env.REACT_APP_COGNITO_CLIENT_SECRET as string | undefined;
const webBaseUrl = getWebBaseUrl();
const redirectUri = (
  usesOwnOrigin()
    ? `${webBaseUrl}/auth/callback`
    : process.env.REACT_APP_COGNITO_REDIRECT_URI || `${webBaseUrl}/auth/callback`
) as string;
const scope = (process.env.REACT_APP_COGNITO_SCOPES || 'openid email profile') as string;
const responseType = (process.env.REACT_APP_COGNITO_RESPONSE_TYPE || 'code') as string;

export const cognitoAuthConfig: AuthProviderProps = {
  authority: computedAuthority || '',
  client_id: clientId || '',
  client_secret: clientSecret,
  redirect_uri: redirectUri,
  response_type: responseType,
  scope,
  post_logout_redirect_uri: webBaseUrl,
  automaticSilentRenew: true,
  loadUserInfo: true,
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  onSigninCallback: () => {
    const returnUrl = sessionStorage.getItem('auth_return_url');
    sessionStorage.removeItem('auth_return_url');
    const targetPath = returnUrl ? resolveOAuthReturnPathFromUrl(returnUrl) : '/';
    window.history.replaceState({}, document.title, targetPath);
    window.dispatchEvent(new PopStateEvent('popstate'));
  },
};

export const cognitoDomain = normalizeCognitoDomain(process.env.REACT_APP_COGNITO_DOMAIN);

export function signOutRedirect() {
  const logoutClientId = process.env.REACT_APP_COGNITO_CLIENT_ID;
  const logoutUri = getWebBaseUrl();

  if (cognitoDomain && logoutClientId) {
    window.location.href = `${cognitoDomain}/logout?client_id=${logoutClientId}&logout_uri=${encodeURIComponent(logoutUri)}`;
  }
}
